import { useMemo } from 'react';

import {
  type BarData,
  DivergingBarChart,
  type DivergingBarData,
  FormatType,
  GaugeChartV2,
  getFormatter,
  getMaxTextWidth,
  getPrimaryColor,
  getPrimaryColorDark,
} from '@revelio/replots';

import {
  useGroupLineages,
  useLineageRows,
} from '../../DataProvider/subsidiaries/SubsidiaryLineageDataProvider';
import { usePrimaryCompanies } from '../../DataProvider/subsidiaries/use-primary-company';
import { useSnapshotDateEnd } from '../../DataProvider/subsidiaries/use-subsidiary-dates';
import { combineQueryStatus } from '../../DataProvider/utils/data-provider-results-utils';
import type { SubsidiarySnapshotKind } from '../../PageConfigProvider/types/item';
import { ChartFallback } from '../ChartFallback';
import { ChartNotSupported } from '../ChartNotSupported';
import { useTableRowsExport } from '../chart-menu/ChartMenuContext';
import { useActivityColors } from '../SubsidiaryTrend/activity-colors';
import {
  countSubsidiaryActivity,
  subsidiaryConcentrationAt,
} from '../SubsidiaryTrend/build-subsidiary-trends';
import { buildSubsidiaryLineage } from '../Timeline/build-subsidiary-lineage';
import styles from './SubsidiarySnapshot.module.css';

const ACQUIRED_LABEL = 'Acquired';
const DIVESTED_LABEL = 'Divested';
const ROW_LABEL_FONT_SIZE = 12;
const DIVERGING_CENTER_GAP = 6;
const DIVERGING_BAR_THICKNESS = 14;
const DIVERGING_BAR_GAP = 16;
const MAX_ROW_LABEL_WIDTH = 140;

const formatCount = getFormatter(FormatType.INTEGER);

type ExportRow = Record<string, string | number>;

const useActivityEnd = (): number => {
  const selected = useSnapshotDateEnd();
  return useMemo(() => selected ?? Date.now(), [selected]);
};

const ConcentrationContent = ({ bars }: { bars: BarData[] }) => {
  const [single] = bars;
  if (bars.every((bar) => bar.value === null)) {
    return (
      <ChartNotSupported>
        {bars.length > 1
          ? 'No headcount for the selected companies’ subsidiaries in our data.'
          : `No headcount for ${single?.label ?? 'this company'}’s subsidiaries in our data.`}
      </ChartNotSupported>
    );
  }

  return (
    <GaugeChartV2
      entities={bars.map((bar) => ({
        value: bar.value ?? 1,
        label: bar.label,
      }))}
      minLabel="Distributed"
      maxLabel="Concentrated"
      colorRange={[getPrimaryColorDark(), getPrimaryColor()]}
      barLabelColor="#25282D"
    />
  );
};

const ConcentrationIndex = () => {
  const companies = usePrimaryCompanies();
  const { lineages, status } = useGroupLineages(companies);
  const at = useActivityEnd();
  const bars = useMemo((): BarData[] | undefined => {
    if (lineages.some((lineage) => lineage === undefined)) return undefined;
    return companies.map((company, index) => {
      const data = lineages[index];
      return {
        id: String(company.rcid),
        label: company.name,
        value:
          data === undefined ? null : subsidiaryConcentrationAt({ data, at }),
      };
    });
  }, [companies, lineages, at]);
  const rows = useMemo(
    (): ExportRow[] =>
      (bars ?? []).map((bar) => ({
        Company: bar.label,
        Concentration: bar.value ?? '',
      })),
    [bars]
  );
  useTableRowsExport(rows);

  if (companies.length === 0) {
    return (
      <ChartNotSupported>
        Select a company to see how concentrated its group is.
      </ChartNotSupported>
    );
  }

  return (
    <ChartFallback status={status}>
      {bars !== undefined && <ConcentrationContent bars={bars} />}
    </ChartFallback>
  );
};

const activityTooltip = (datum: DivergingBarData) => (
  <div className={styles.tooltipBlock}>
    <div className={styles.tooltipTitle}>{datum.label}</div>
    <dl className={styles.tooltipMetrics}>
      <dt>{ACQUIRED_LABEL}</dt>
      <dd>{formatCount(datum.rightValue ?? 0)}</dd>
      <dt>{DIVESTED_LABEL}</dt>
      <dd>{formatCount(Math.abs(datum.leftValue ?? 0))}</dd>
    </dl>
  </div>
);

const activityValueLabel = (
  datum: DivergingBarData,
  side: 'left' | 'right'
): string =>
  formatCount(
    Math.abs((side === 'left' ? datum.leftValue : datum.rightValue) ?? 0)
  );

const AcquiredVsSpunOff = () => {
  const { acquired, divested } = useActivityColors();
  const companies = usePrimaryCompanies();
  const rcids = useMemo(
    () => companies.map((company) => company.rcid),
    [companies]
  );
  const { rows, statuses } = useLineageRows(rcids);
  const to = useActivityEnd();

  const data = useMemo((): DivergingBarData[] | undefined => {
    if (rows.some((companyRows) => companyRows === undefined)) {
      return undefined;
    }
    return companies.map((company, index) => {
      const activity = countSubsidiaryActivity({
        data: buildSubsidiaryLineage({
          rows: rows[index] ?? [],
          root: company,
          companies: [],
        }),
        from: Number.NEGATIVE_INFINITY,
        to,
      });
      return {
        label: company.name,
        leftValue: -activity.left,
        rightValue: activity.joined,
        leftLabel: DIVESTED_LABEL,
        rightLabel: ACQUIRED_LABEL,
      };
    });
  }, [companies, rows, to]);

  const exportRows = useMemo(
    (): ExportRow[] =>
      (data ?? []).map((datum) => ({
        Company: datum.label,
        [ACQUIRED_LABEL]: datum.rightValue ?? 0,
        [DIVESTED_LABEL]: Math.abs(datum.leftValue ?? 0),
      })),
    [data]
  );
  useTableRowsExport(exportRows);

  if (companies.length === 0) {
    return (
      <ChartNotSupported>
        Select a company to see its acquisitions and disposals.
      </ChartNotSupported>
    );
  }

  const labelWidth = Math.min(
    getMaxTextWidth({
      texts: companies.map((company) => company.name),
      fontSize: ROW_LABEL_FONT_SIZE,
    }),
    MAX_ROW_LABEL_WIDTH
  );
  const quiet =
    data !== undefined &&
    data.every((datum) => datum.leftValue === 0 && datum.rightValue === 0);

  return (
    <ChartFallback status={combineQueryStatus(statuses)}>
      {quiet ? (
        <ChartNotSupported>
          No subsidiary joined or left the group in our data.
        </ChartNotSupported>
      ) : (
        data !== undefined && (
          <DivergingBarChart
            data={data}
            format={FormatType.INTEGER}
            leftColor={divested}
            rightColor={acquired}
            centerGap={DIVERGING_CENTER_GAP}
            barThickness={DIVERGING_BAR_THICKNESS}
            barGap={DIVERGING_BAR_GAP}
            leftHeaderLabel={DIVESTED_LABEL}
            rightHeaderLabel={ACQUIRED_LABEL}
            showBarLabels
            valueLabel={activityValueLabel}
            tooltipContent={activityTooltip}
            sortData={false}
            barLabelPosition="outside"
            showYAxisLabels={data.length > 1}
            labelWidth={labelWidth}
          />
        )
      )}
    </ChartFallback>
  );
};

export const SubsidiarySnapshot = ({
  kind,
}: {
  kind: SubsidiarySnapshotKind;
}) =>
  kind === 'concentration' ? <ConcentrationIndex /> : <AcquiredVsSpunOff />;
